/**
 * ADR-70 cut 6g (hitlist 19): the ring console's "Sale checks" panel.
 *
 * The console reads the sale health report (GET /sales/:saleId/health) once
 * a minute while the page is in view, and shows its counts, the top errors
 * and warnings, and the advisories folded away. Fixing happens in the office
 * (admin's Sale health page); the ring only needs to know.
 *
 * The one rule this file keeps: a check that could not be read is never
 * shown as clear. Before the first answer the panel says it is checking;
 * after a failure with nothing read it says the checks are unavailable; a
 * failure after a good read keeps that report and says how old it is.
 *
 * Pure, so the rules are testable without a browser. The caller draws every
 * string as a text node: a lot number or a seller's name is untrusted.
 */

export const READ_EVERY_MS = 60_000;
/** A failed read is tried again on the same minute; the floor is not
 *  hammered by a console whose API is down. */
export const READ_AFTER_FAILURE_MS = 60_000;
/** The top lines shown open; the rest are counted. */
export const TOP_LINES = 4;
/** Lot numbers listed under a line before "and N more". */
export const HIPS_SHOWN = 6;

export type CheckSeverity = 'error' | 'warning' | 'advisory';
export type CheckVerdict = 'errors' | 'warnings' | 'advisories' | 'clear';

export interface CheckFinding {
  readonly severity: CheckSeverity;
  readonly message: string;
  readonly hips: readonly string[];
  readonly fingerprint: string;
}

export interface SaleChecksReport {
  readonly verdict: CheckVerdict;
  readonly generatedAt: number;
  readonly summary: { readonly errors: number; readonly warnings: number; readonly advisories: number; readonly passed: number; readonly notChecked: number };
  readonly errors: readonly CheckFinding[];
  readonly warnings: readonly CheckFinding[];
  readonly advisories: readonly CheckFinding[];
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
const VERDICTS: readonly string[] = ['errors', 'warnings', 'advisories', 'clear'];

function parseFindings(raw: unknown, severity: CheckSeverity): CheckFinding[] | null {
  if (!Array.isArray(raw)) return null;
  const out: CheckFinding[] = [];
  for (const f of raw) {
    if (!isObj(f) || typeof f['message'] !== 'string') return null;
    const hips = Array.isArray(f['affectedHips']) ? f['affectedHips'].filter((h): h is string => typeof h === 'string') : [];
    out.push({ severity, message: f['message'], hips, fingerprint: typeof f['fingerprint'] === 'string' ? f['fingerprint'] : `${severity}:${out.length}` });
  }
  return out;
}

/**
 * The report, or null when the reply is not one. A reply that cannot be read
 * counts as a failed check, never as an empty (clear) one: the verdict and
 * the counts must be there and must agree with the lists.
 */
export function parseSaleHealth(raw: unknown): SaleChecksReport | null {
  if (!isObj(raw) || !isObj(raw['summary'])) return null;
  const verdict = raw['verdict'];
  if (typeof verdict !== 'string' || !VERDICTS.includes(verdict)) return null;
  const s = raw['summary'];
  if (!isCount(s['errors']) || !isCount(s['warnings']) || !isCount(s['advisories']) || !isCount(s['passed']) || !isCount(s['notChecked'])) return null;
  const errors = parseFindings(raw['errors'], 'error');
  const warnings = parseFindings(raw['warnings'], 'warning');
  const advisories = parseFindings(raw['advisories'], 'advisory');
  if (!errors || !warnings || !advisories) return null;
  if (errors.length !== s['errors'] || warnings.length !== s['warnings'] || advisories.length !== s['advisories']) return null;
  // "clear" with something in it is not a report this panel will believe.
  if (verdict === 'clear' && errors.length + warnings.length + advisories.length > 0) return null;
  const at = typeof raw['generatedAt'] === 'string' ? Date.parse(raw['generatedAt']) : NaN;
  return {
    verdict: verdict as CheckVerdict,
    generatedAt: Number.isFinite(at) ? at : 0,
    summary: { errors: s['errors'], warnings: s['warnings'], advisories: s['advisories'], passed: s['passed'], notChecked: s['notChecked'] },
    errors, warnings, advisories,
  };
}

/** NONE: not read yet. LOADING: the first read is out. READ: the last read
 *  came back. FAILED: the last read did not (a report from before may stand). */
export type ChecksStatus = 'NONE' | 'LOADING' | 'READ' | 'FAILED';

export class SaleChecks {
  status: ChecksStatus = 'NONE';
  report: SaleChecksReport | null = null;
  /** When the standing report was read (the console's clock). */
  readAt: number | null = null;
  busy = false;
  private lastTry: number | null = null;

  /** Time for a read: never two at once, and only while the page is seen. */
  shouldRead(now: number, visible: boolean): boolean {
    if (this.busy || !visible) return false;
    if (this.lastTry === null) return true;
    const every = this.status === 'FAILED' ? READ_AFTER_FAILURE_MS : READ_EVERY_MS;
    return now - this.lastTry >= every;
  }

  begin(now: number): void {
    this.busy = true;
    this.lastTry = now;
    if (this.status === 'NONE') this.status = 'LOADING';
  }

  /** The answer, or null for a read that failed or could not be parsed. */
  settle(now: number, report: SaleChecksReport | null): void {
    this.busy = false;
    if (report === null) { this.status = 'FAILED'; return; }
    this.status = 'READ';
    this.report = report;
    this.readAt = now;
  }
}

export type ChecksTone = 'bad' | 'warn' | 'info' | 'ok' | 'muted';

export interface ChecksLine {
  readonly key: string;
  readonly tone: 'bad' | 'warn' | 'info';
  readonly label: string;
  readonly text: string;
  readonly hips: string | null;
}

export interface ChecksView {
  /** The panel head's right-hand words, and their tone. */
  readonly head: string;
  readonly tone: ChecksTone;
  /** Errors, then warnings, up to TOP_LINES. */
  readonly lines: readonly ChecksLine[];
  /** "3 more errors and warnings" past the top lines, or null. */
  readonly more: string | null;
  readonly advisories: readonly ChecksLine[];
  /** One line under the list: unavailable, how old, or where to fix. */
  readonly note: string | null;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "Hip 3, 7" / "Lot 3, 7": the sale's word (`saleWords().cap`) is always passed, never assumed. */
export function hipsLine(hips: readonly string[], term: string): string | null {
  if (hips.length === 0) return null;
  const shown = hips.slice(0, HIPS_SHOWN).join(', ');
  const rest = hips.length - HIPS_SHOWN;
  return `${term} ${shown}${rest > 0 ? ` and ${rest} more` : ''}`;
}

function minutesAgo(then: number, now: number): string {
  const mins = Math.max(0, Math.round((now - then) / 60_000));
  return mins < 1 ? 'under a minute ago' : `${plural(mins, 'minute', 'minutes')} ago`;
}

const LABEL: Record<CheckSeverity, { tone: 'bad' | 'warn' | 'info'; label: string }> = {
  error: { tone: 'bad', label: 'Error' },
  warning: { tone: 'warn', label: 'Warning' },
  advisory: { tone: 'info', label: 'Advisory' },
};

const toLine = (f: CheckFinding, term: string): ChecksLine => ({
  key: f.fingerprint, tone: LABEL[f.severity].tone, label: LABEL[f.severity].label, text: f.message, hips: hipsLine(f.hips, term),
});

export function checksView(c: Pick<SaleChecks, 'status' | 'report' | 'readAt'>, now: number, term: string): ChecksView {
  const r = c.report;
  if (r === null) {
    if (c.status === 'FAILED') {
      return { head: 'Unavailable', tone: 'warn', lines: [], more: null, advisories: [], note: 'The sale checks could not be read. That is not a clear; the office can see them on Sale health.' };
    }
    return { head: 'Checking…', tone: 'muted', lines: [], more: null, advisories: [], note: null };
  }

  const open = [...r.errors, ...r.warnings];
  const lines = open.slice(0, TOP_LINES).map((f) => toLine(f, term));
  const rest = open.length - lines.length;
  const more = rest > 0 ? `${rest} more ${rest === 1 ? 'error or warning' : 'errors and warnings'}` : null;

  let head: string;
  let tone: ChecksTone;
  if (r.summary.errors > 0) {
    head = [plural(r.summary.errors, 'error', 'errors'), r.summary.warnings > 0 ? plural(r.summary.warnings, 'warning', 'warnings') : null].filter(Boolean).join(' · ');
    tone = 'bad';
  } else if (r.summary.warnings > 0) {
    head = plural(r.summary.warnings, 'warning', 'warnings');
    tone = 'warn';
  } else if (r.summary.advisories > 0) {
    head = `No errors · ${plural(r.summary.advisories, 'advisory', 'advisories')}`;
    tone = 'info';
  } else if (c.status === 'FAILED') {
    // An older clear is not a clear now.
    head = 'Not confirmed';
    tone = 'warn';
  } else {
    head = 'All clear';
    tone = 'ok';
  }

  let note: string | null = null;
  if (c.status === 'FAILED' && c.readAt !== null) {
    note = `The last check failed. This is the one from ${minutesAgo(c.readAt, now)}.`;
  } else if (open.length > 0) {
    note = 'Fixes are made in the office, on Sale health.';
  }
  return { head, tone, lines, more, advisories: r.advisories.map((f) => toLine(f, term)), note };
}
